import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from './Button';
import { CopyButton } from './CopyButton';
import { useToast } from './Toast';
import { useSpeech } from '../hooks/useSpeech';
import { CloseIcon, RefreshIcon, SquareIcon, ThumbDownIcon, ThumbUpIcon, VolumeIcon } from './icons';
import './MessageActions.css';

// Motifs d'un 👎 (contrat C0, §3)
const REASONS = [
  { id: 'chiffre_faux', label: 'Chiffre faux' },
  { id: 'visuel_inadapte', label: 'Visuel inadapté' },
  { id: 'incomplet', label: 'Incomplet' },
  { id: 'lent', label: 'Lent' },
  { id: 'autre', label: 'Autre' },
];
const COMMENT_MAX = 500;

function DislikePopover({ onSubmit, onClose, sending, anchorRef }) {
  const [reason, setReason] = useState(null);
  const [comment, setComment] = useState('');
  const rootRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const titleId = useId();

  // Focus sur le premier motif à l'ouverture ; clic à l'extérieur = fermeture
  useEffect(() => {
    rootRef.current?.querySelector('.feedback-reason')?.focus();
    const onPointerDown = (event) => {
      if (rootRef.current?.contains(event.target) || anchorRef.current?.contains(event.target)) return;
      onCloseRef.current(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [anchorRef]);

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
    }
  };

  return (
    <motion.div
      ref={rootRef}
      className="feedback-popover"
      role="dialog"
      aria-labelledby={titleId}
      onKeyDown={onKeyDown}
      initial={{ opacity: 0, y: 6, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 4 }}
      transition={{ duration: 0.16 }}
    >
      <div className="feedback-popover-header">
        <p id={titleId} className="feedback-popover-title">Qu'est-ce qui n'allait pas ?</p>
        <Button variant="ghost" size="sm" icon={CloseIcon} iconOnly className="feedback-icon-button" onClick={() => onClose()} aria-label="Fermer" />
      </div>

      <div className="feedback-reasons" role="group" aria-label="Motif">
        {REASONS.map((r) => (
          <button
            key={r.id}
            type="button"
            className={`feedback-reason ${reason === r.id ? 'is-selected' : ''}`}
            aria-pressed={reason === r.id}
            onClick={() => setReason(reason === r.id ? null : r.id)}
          >
            {r.label}
          </button>
        ))}
      </div>

      <label className="feedback-comment">
        <span className="sr-only">Commentaire (facultatif)</span>
        <textarea
          value={comment}
          maxLength={COMMENT_MAX}
          rows={3}
          placeholder="Commentaire (facultatif)"
          onChange={(event) => setComment(event.target.value)}
        />
        <span className="feedback-counter" aria-hidden="true">{comment.length}/{COMMENT_MAX}</span>
      </label>

      <div className="feedback-popover-footer">
        <Button
          variant="primary"
          size="sm"
          className="feedback-submit"
          loading={sending}
          onClick={() => onSubmit({ reason, comment: comment.trim() })}
        >
          Envoyer
        </Button>
      </div>
    </motion.div>
  );
}

/**
 * Barre d'actions sous une réponse terminée : copier, 👍, 👎 (avec motif), écouter,
 * régénérer (dernière réponse uniquement). Un nouveau clic sur le pouce actif annule l'avis.
 */
export function MessageActions({ message, onRate, onRegenerate = null, busy = false, children }) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const dislikeRef = useRef(null);
  const toast = useToast();
  const rating = message.feedback?.rating ?? null;
  const sending = message.feedback?.status === 'sending';
  const canRate = !!message.serverMessageId && !!onRate;

  const rate = async (value, details) => {
    try {
      await onRate(message.id, value, details);
      toast(value === null ? 'Avis retiré' : 'Merci pour votre retour');
      return true;
    } catch {
      toast("Impossible d'enregistrer votre avis", { tone: 'error' });
      return false;
    }
  };

  // Retour du focus sur 👎, sauf si l'utilisateur a cliqué ailleurs
  const closePopover = (restoreFocus = true) => {
    setPopoverOpen(false);
    if (restoreFocus) dislikeRef.current?.focus();
  };

  const onLike = () => {
    setPopoverOpen(false);
    rate(rating === 1 ? null : 1);
  };

  const onDislike = () => {
    if (rating === -1) {
      rate(null);
    } else {
      setPopoverOpen((open) => !open);
    }
  };

  const onSubmitDislike = async (details) => {
    const ok = await rate(-1, details);
    if (ok) closePopover();
  };

  const unavailable = canRate ? undefined : 'Avis indisponible pour ce message';
  const getText = useCallback(() => message.content, [message.content]);
  // Échec de la lecture (aucune voix, erreur de synthèse) ou avertissement : toast
  const notifySpeech = useCallback((text, tone) => toast(text, { tone }), [toast]);
  const speech = useSpeech(message.id, getText, notifySpeech);

  return (
    <div className="message-actions">
      <div className="message-actions-buttons">
        <CopyButton
          getText={() => message.content}
          label="Copier la réponse"
          className="message-action"
        />

        <Button
          variant="ghost"
          size="sm"
          icon={ThumbUpIcon}
          iconSize={15}
          iconOnly
          className="message-action"
          aria-pressed={rating === 1}
          aria-label="Bonne réponse"
          title={unavailable ?? (rating === 1 ? 'Retirer mon avis' : 'Bonne réponse')}
          disabled={!canRate || sending}
          onClick={onLike}
        />

        <div className="message-action-anchor">
          <Button
            ref={dislikeRef}
            variant="ghost"
            size="sm"
            icon={ThumbDownIcon}
            iconSize={15}
            iconOnly
            className={`message-action ${rating === -1 ? 'is-negative' : ''}`}
            aria-pressed={rating === -1}
            aria-haspopup="dialog"
            aria-expanded={popoverOpen}
            aria-label="Mauvaise réponse"
            title={unavailable ?? (rating === -1 ? 'Retirer mon avis' : 'Mauvaise réponse')}
            disabled={!canRate || sending}
            onClick={onDislike}
          />

          <AnimatePresence>
            {popoverOpen && (
              <DislikePopover
                onSubmit={onSubmitDislike}
                onClose={closePopover}
                sending={sending}
                anchorRef={dislikeRef}
              />
            )}
          </AnimatePresence>
        </div>

        {/* Lecture à voix haute (masquée si le navigateur ne la propose pas) ; pendant la
            lecture : bouton Arrêter et barres animées (figées si le mouvement est réduit) */}
        {speech.supported && (
          <Button
            variant="ghost"
            size="sm"
            icon={speech.speaking ? SquareIcon : VolumeIcon}
            iconSize={speech.speaking ? 12 : 15}
            iconOnly={!speech.speaking}
            className="message-action"
            aria-pressed={speech.speaking}
            aria-label="Écouter la réponse"
            title={speech.speaking ? 'Arrêter la lecture' : 'Écouter'}
            onClick={speech.toggle}
          >
            {speech.speaking && (
              <span className="speech-bars" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
            )}
          </Button>
        )}

        {onRegenerate && (
          <Button
            variant="ghost"
            size="sm"
            icon={RefreshIcon}
            iconSize={15}
            iconOnly
            className="message-action"
            aria-label="Régénérer la réponse"
            title="Régénérer la réponse"
            disabled={busy}
            onClick={onRegenerate}
          />
        )}
      </div>

      {children}
    </div>
  );
}
